/**
 * Animate the browser-tab title.
 *
 * `"typewriter"` mode: types out the text one character at a time, pauses
 * at the full string, deletes back to empty, pauses again, loops. The
 * visual rhythm matches link-in-bio sites like guns.lol where the tab
 * title keeps catching your eye in the browser's tab strip.
 *
 * `"none"` mode: sets the title once and does nothing else — same as the
 * classic `document.title = "..."` baseline.
 *
 * Returns a cleanup function that stops the timer and restores the final
 * text. Must be called before re-registering (otherwise two concurrent
 * loops will fight over `document.title`). In practice the profile's
 * renderFromConfig teardown flow already threads this through
 * `activeCleanups`.
 */
export function animateTitle(
  text: string,
  style: "none" | "typewriter",
): () => void {
  // Empty strings would loop instantly with nothing to show — bail out
  // to avoid burning a timer tick on a no-op.
  if (!text) {
    document.title = text;
    return () => {};
  }

  if (style === "none") {
    document.title = text;
    return () => {};
  }

  // Typewriter tuning. Kept local rather than schema-driven because
  // there's basically one "right" cadence for tab titles — exposing
  // knobs here clutters admin without meaningful payoff.
  //
  // We deliberately do NOT animate a char-by-char deletion. At 80ms/char
  // the chrome tab strip is too narrow to clearly render intermediate
  // states, so the "untyping" blurs together and reads as an abrupt
  // flash. The simpler type → linger → snap-to-blank → retype pattern
  // matches guns.lol and reads as a clean typewriter loop.
  const TYPE_MS = 120;
  const PAUSE_FULL_MS = 2500; // how long the complete title sits visible
  const PAUSE_EMPTY_MS = 700; // blank gap between wipe and retype

  type Phase = "typing" | "pause-full" | "pause-empty";
  let phase: Phase = "typing";
  let cursor = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const tick = () => {
    if (stopped) return;

    switch (phase) {
      case "typing":
        cursor++;
        document.title = text.slice(0, cursor);
        if (cursor >= text.length) {
          phase = "pause-full";
          timer = setTimeout(tick, PAUSE_FULL_MS);
        } else {
          timer = setTimeout(tick, TYPE_MS);
        }
        break;

      case "pause-full":
        // Snap to blank. The visible "deletion" is this single title write
        // rather than a per-character animation.
        document.title = "";
        cursor = 0;
        phase = "pause-empty";
        timer = setTimeout(tick, PAUSE_EMPTY_MS);
        break;

      case "pause-empty":
        phase = "typing";
        timer = setTimeout(tick, TYPE_MS);
        break;
    }
  };

  // Start empty and let the first tick paint the first character; gives
  // a clean "tab just loaded, now typing" feel rather than flashing the
  // full title first.
  document.title = "";
  timer = setTimeout(tick, TYPE_MS);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    // Leave the tab with the full text — useful if the user glances at
    // the tab during admin-panel teardown or page unload.
    document.title = text;
  };
}
